import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, withKeys as _withKeys, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "buttons-container" }

import ResumeBase from "@/Layouts/ResumeBase/ResumeBase.vue";
import TextInput from "@/components/Common/Inputs/TextInput/TextInput.vue";
import TextAreaInput from "@/components/Common/Inputs/TextAreaInput/TextAreaInput.vue";
import CheckboxInput from "@/components/Common/Inputs/CheckboxInput/CheckboxInput.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { WorkExperience } from "@/store/models/resume.models";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, onBeforeRouteLeave, useRoute } from "vue-router";
import { onIonViewDidEnter } from "@ionic/vue";
import { handleBeforeUnload, focusOnMainContent } from "@/helpers";
import {
  IonInputCustomEvent,
  IonInput,
  IonTextareaCustomEvent,
} from "@ionic/core/components";
import {
  collateErrorsInDescription,
  parseDate,
  validateDateFormat,
} from "@/helpers";


export default /*@__PURE__*/_defineComponent({
  __name: 'ResumeWorkExperienceDetail',
  setup(__props) {

const { t } = useI18n<{}>({
  useScope: "global",
});
const resumeStore = useResumeStore();
const errorStore = useErrorStore();
const router = useRouter();
const route = useRoute();

const skipOnRouteBeforeLeave = ref(false);
const triggerDeleteModal = ref(false);

onBeforeRouteLeave(() => {
  resumeStore.setWorkExperienceFormInit(false);
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

onMounted(async () => {
  if (!resumeStore.resumeId) {
    await resumeStore.requestResume();
    if (!resumeStore.resumeId) {
      await resumeStore.createResume();
    }
  }
  const workExperienceId = router.currentRoute.value.params.id as string;
  workExperienceId !== "new"
    ? resumeStore.initWorkExperienceForm(workExperienceId)
    : resumeStore.initWorkExperienceForm();
  initValidationMap();
});

onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
  skipOnRouteBeforeLeave.value = false;
  window.addEventListener("beforeunload", handleBeforeUnload);
});

const workExperienceId = computed(() => {
  return resumeStore.workExperienceFormData?.id;
});

const role = computed(() => {
  return resumeStore.workExperienceFormData?.role;
});

const business = computed(() => {
  return resumeStore.workExperienceFormData?.business;
});

const startDate = computed(() => {
  return resumeStore.workExperienceFormData?.startDate;
});

const endDate = computed(() => {
  return resumeStore.workExperienceFormData?.endDate;
});

const isCurrentRole = computed(() => {
  return resumeStore.workExperienceFormData?.isCurrentRole;
});

const businessDescription = computed(() => {
  return resumeStore.workExperienceFormData?.businessDescription;
});

const impact = computed(() => {
  return resumeStore.workExperienceFormData?.impact;
});

const isFormSaving = ref(false);
const isWorkExperienceDeleting = ref(false);

const setValue = <K extends keyof WorkExperience>(
  property: K,
  value: WorkExperience[K] | null | undefined
) => {
  if (value == null || !property) return;
  resumeStore.setWorkExperienceFormValue(property, value);
};

const saveForm = async () => {
  skipOnRouteBeforeLeave.value = true;
  errorStore.setSaveFormError(false);
  try {
    isFormSaving.value = true;
    if (workExperienceId.value) {
      await resumeStore.updateWorkExperience(workExperienceId.value);
    } else {
      await resumeStore.createWorkExperience();
      // TODO: When do we declare that work experiences are completed?
      await resumeStore.updateResumeProperty(
        "isWorkExperiencesCompleted",
        true
      );
    }
    router.push({
      name: "ResumeWorkExperiences",
    });
  } catch (error) {
    console.error("An error occured saving the form: ", error);
    errorStore.setSaveFormError(true);
  } finally {
    isFormSaving.value = false;
  }
};

const triggerDelete = () => {
  triggerDeleteModal.value = true;
};

const deleteWorkExperience = async () => {
  try {
    skipOnRouteBeforeLeave.value = true;
    isWorkExperienceDeleting.value = true;
    if (workExperienceId.value) {
      await resumeStore.deleteWorkExperience(workExperienceId.value);
    } else {
      throw new Error("Something went wrong");
    }
    router.push({
      name: "ResumeWorkExperiences",
    });
  } finally {
    isWorkExperienceDeleting.value = false;
    triggerDeleteModal.value = false;
  }
};

const dismissDeleteModal = () => {
  triggerDeleteModal.value = false;
};

const isCurrentRoleChange = (checked: boolean) => {
  setValue("isCurrentRole", checked);
  if (checked) {
    setValue("endDate", "");
    validationMap.value.set("endDate", true);
    const itemEl = document.querySelector(".end-date-input ion-item");
    if (itemEl) {
      itemEl.classList.remove("ion-invalid");
    }
    return;
  }
};

const errorIdPrefix = ref("error-message-disabler");
const saveButtonDescription = ref("");
const updateErrorDescription = () => {
  saveButtonDescription.value = collateErrorsInDescription(
    document,
    errorIdPrefix.value
  );
};

/* Impact input bullet point handling */

/**
 * Add the initial bullet point to the text area if there is no text
 * @param event Focus event
 */
const initialBulletPoint = (
  event: IonTextareaCustomEvent<FocusEvent>
): void => {
  if (event.target.value === "") {
    setValue("impact", "• ");
  }
};

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData("text");
  const lines = pastedText?.split("\n");

  const sanitisedPastedText = lines
    ?.map((line) => {
      const trimmedLine = line.trim();
      const listIdRegex =
        /^(\d+\.\s|\d+\)\s|[-*•+#→>]\s|^[a-zA-Z]\.\s|^[a-zA-Z]\)\s)/;

      if (listIdRegex.test(trimmedLine)) {
        return "• " + trimmedLine.replace(listIdRegex, "");
      }
      return "• " + trimmedLine;
    })
    .join("\n");

  const target = event.target as HTMLTextAreaElement;
  const existingLines = target.value?.split("\n");

  let result;
  if (existingLines) {
    while (
      existingLines.length > 0 &&
      existingLines[existingLines.length - 1].trim() === "•"
    ) {
      // Remove all empty bullet points
      existingLines.pop();
    }

    const existingText = existingLines.join("\n");
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;

    result = existingText
      ? existingText.slice(0, selectionStart) +
        "\n" +
        sanitisedPastedText +
        existingText.slice(selectionEnd)
      : undefined;
  }

  setValue("impact", result ?? sanitisedPastedText);
};

/**
 * Removes empty bullet point from string
 * @param event Focus event
 */
const removeBulletPoint = (event: IonTextareaCustomEvent<FocusEvent>): void => {
  const text = event.target.value;
  const sanitisedText = text
    ?.split("\n")
    .filter((line) => line.trim() !== "•" && line.trim() !== "")
    .join("\n");
  setValue("impact", sanitisedText);
};

/**
 * Injects a bullet point in a new line when the enter key is pressed
 * @param event Keyboard enter event
 */
const injectBulletPointOnEnter = (event: KeyboardEvent): void => {
  if (event.key === "Enter") {
    event.preventDefault();

    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    const selectionStart = target.selectionStart;
    const selectionEnd = target.selectionEnd;

    // Split the text at the cursor position
    const beforeCursor = value.substring(0, selectionStart);
    const afterCursor = value.substring(selectionEnd);

    setValue("impact", `${beforeCursor}\n• ${afterCursor}`);
  }
};

const returnToPage = () => {
  router.push({
    name: "ResumeWorkExperiences",
  });
};

/* VALIDATION */

const rules = {
  role: {
    required: t("resume.workExperienceForm.roleInput.isRequiredError"),
  },
  startDate: {
    invalidFormat: t(
      "resume.workExperienceForm.startDateInput.invalidFormatError"
    ),
    noFutureDate: t(
      "resume.workExperienceForm.startDateInput.noFutureDateError"
    ),
  },
  endDate: {
    invalidFormat: t(
      "resume.workExperienceForm.endDateInput.invalidFormatError"
    ),
    noFutureDate: t("resume.workExperienceForm.endDateInput.noFutureDateError"),
    noBeforeStartDate: t(
      "resume.workExperienceForm.endDateInput.noBeforeStartDateError"
    ),
  },
  impact: {
    characterLimit: t(
      "resume.workExperienceForm.impactInput.characterLimitError"
    ),
  },
  businessDescription: {
    characterLimit: t(
      "resume.workExperienceForm.businessDescriptionInput.characterLimitError"
    ),
  },
};

const validationMap = ref<Map<keyof WorkExperience, boolean>>(new Map([]));
const initValidationMap = () => {
  validationMap.value
    .set("role", role.value !== undefined && role.value.length > 0)
    .set("startDate", true)
    .set("endDate", true);
};

const errorMessageMap = ref<Map<keyof WorkExperience, string>>(
  new Map([
    ["role", ""],
    ["endDate", ""],
    ["startDate", ""],
    ["impact", ""],
  ])
);

const setValidity = (
  property: keyof WorkExperience,
  valid: boolean,
  error?: string
): void => {
  validationMap.value.set(property, valid);
  errorMessageMap.value.set(property, error ?? "");
  return;
};

const isFormSavingEnabled = computed(() => {
  for (let isValid of validationMap.value.values()) {
    if (!isValid) return false;
  }
  return true;
});

const validateRole = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const name = (event.target.value as string).trim();
  if (!name) {
    itemEl?.classList.add("ion-invalid");
    setValidity("role", false, rules.role.required);
  } else {
    setValidity("role", true);
  }
};

const validateStartDate = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const date = event.target.value as string;
  if (!date) {
    setValidity("startDate", true);
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = parseDate(date);
  if (!validateDateFormat(date)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("startDate", false, rules.startDate.invalidFormat);
  } else if (inputDate && inputDate > today) {
    itemEl?.classList.add("ion-invalid");
    setValidity("startDate", false, rules.startDate.noFutureDate);
  } else {
    setValidity("startDate", true);
  }

  // This is required to dynamically validate the end date when the issue date changes
  const endDateEl = document.querySelector(
    ".end-date-input .input-field"
  ) as unknown as IonInput;
  if (endDateEl) {
    endDateEl.setFocus();
    endDateEl.setBlur();
  }
};

const validateEndDate = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  if (isCurrentRole.value) {
    setValue("endDate", "");
    setValidity("endDate", true);
    return;
  }
  const date = event.target.value as string;
  if (!date) {
    setValidity("endDate", true);
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = parseDate(date);
  const parsedStartDate = parseDate(startDate.value || "");
  if (!validateDateFormat(date)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.invalidFormat);
  } else if (inputDate && inputDate > today) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.noFutureDate);
  } else if (parsedStartDate && inputDate && inputDate < parsedStartDate) {
    itemEl?.classList.add("ion-invalid");
    setValidity("endDate", false, rules.endDate.noBeforeStartDate);
  } else {
    setValidity("endDate", true);
  }
};

const validateImpact = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const highlights = event.target.value as string;
  if (highlights && highlights.length >= 600) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set("impact", rules.impact.characterLimit);
  }
};

const validateBusinessDescription = (
  event: IonInputCustomEvent<FocusEvent>
) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const highlights = event.target.value as string;
  if (highlights && highlights.length >= 600) {
    itemEl?.classList.add("ion-invalid");
    errorMessageMap.value.set(
      "businessDescription",
      rules.businessDescription.characterLimit
    );
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ResumeBase, {
    breadcrumbs: [
      {
        route: 'my-experience',
        label: _unref(t)('resume.breadcrumbs.workExperiences'),
        type: 'parent',
      },
      {
        label: _unref(t)('resume.breadcrumbs.workExperienceDetail'),
      },
    ],
    mainContentId: "resume-work-experience-detail-main-content",
    formSaveRetryFunction: saveForm,
    skipOnRouteBeforeLeave: skipOnRouteBeforeLeave.value,
    formDeleteFunction: deleteWorkExperience,
    triggerDeleteModal: triggerDeleteModal.value,
    dismissDeleteModal: dismissDeleteModal
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.workExperienceForm.title")), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.workExperienceForm.description")), 1)
    ]),
    "main-content": _withCtx(() => [
      _createElementVNode("form", {
        "aria-label": _unref(t)('resume.workExperienceForm.title')
      }, [
        _createVNode(TextInput, {
          "data-test": "role-input",
          errorText: errorMessageMap.value.get('role'),
          labelText: _unref(t)('resume.workExperienceForm.roleInput.label'),
          placeholderText: 
            _unref(t)('resume.workExperienceForm.roleInput.placeholder')
          ,
          value: role.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          required: true,
          errorIdPrefix: errorIdPrefix.value,
          onOnChange: _cache[0] || (_cache[0] = 
            (event) => {
              validateRole(event);
              setValue('role', event.detail.value);
            }
          ),
          onOnBlur: validateRole,
          onOnUpdate: updateErrorDescription
        }, null, 8, ["errorText", "labelText", "placeholderText", "value", "isLoading", "errorIdPrefix"]),
        _createVNode(TextInput, {
          "data-test": "business-input",
          labelText: _unref(t)('resume.workExperienceForm.businessInput.label'),
          placeholderText: 
            _unref(t)('resume.workExperienceForm.businessInput.placeholder')
          ,
          value: business.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[1] || (_cache[1] = (event) => setValue('business', event.detail.value))
        }, null, 8, ["labelText", "placeholderText", "value", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "start-date-input",
          class: "start-date-input",
          errorText: errorMessageMap.value.get('startDate'),
          labelText: _unref(t)('resume.workExperienceForm.startDateInput.label'),
          placeholderText: 
            _unref(t)('resume.workExperienceForm.startDateInput.placeholder')
          ,
          value: startDate.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          errorIdPrefix: errorIdPrefix.value,
          onOnChange: _cache[2] || (_cache[2] = (event) => setValue('startDate', event.detail.value)),
          onOnBlur: validateStartDate,
          onOnUpdate: updateErrorDescription
        }, null, 8, ["errorText", "labelText", "placeholderText", "value", "isLoading", "errorIdPrefix"]),
        _createVNode(CheckboxInput, {
          "data-test": "is-current-role-input",
          checked: isCurrentRole.value || false,
          labelText: _unref(t)('resume.workExperienceForm.isCurrentRoleInput.label'),
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[3] || (_cache[3] = (checked) => isCurrentRoleChange(checked))
        }, null, 8, ["checked", "labelText", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "end-date-input",
          class: "end-date-input",
          errorText: errorMessageMap.value.get('endDate'),
          labelText: _unref(t)('resume.workExperienceForm.endDateInput.label'),
          placeholderText: 
            _unref(t)('resume.workExperienceForm.endDateInput.placeholder')
          ,
          value: endDate.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          disabled: isCurrentRole.value,
          errorIdPrefix: errorIdPrefix.value,
          onOnChange: _cache[4] || (_cache[4] = (event) => setValue('endDate', event.detail.value)),
          onOnBlur: validateEndDate,
          onOnUpdate: updateErrorDescription
        }, null, 8, ["errorText", "labelText", "placeholderText", "value", "isLoading", "disabled", "errorIdPrefix"]),
        _createVNode(TextAreaInput, {
          "data-test": "business-description-input",
          class: "business-description-input",
          labelText: 
            _unref(t)('resume.workExperienceForm.businessDescriptionInput.label')
          ,
          errorText: errorMessageMap.value.get('businessDescription'),
          maxLength: 600,
          placeholderText: 
            _unref(t)('resume.workExperienceForm.businessDescriptionInput.placeholder')
          ,
          autogrow: true,
          value: businessDescription.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[5] || (_cache[5] = 
            (event) => {
              setValue('businessDescription', event.detail.value);
              validateBusinessDescription(event);
            }
          )
        }, null, 8, ["labelText", "errorText", "placeholderText", "value", "isLoading"]),
        _createVNode(TextAreaInput, {
          "data-test": "impact-input",
          labelText: _unref(t)('resume.workExperienceForm.impactInput.label'),
          errorText: errorMessageMap.value.get('impact'),
          maxLength: 600,
          placeholderText: 
            _unref(t)('resume.workExperienceForm.impactInput.placeholder')
          ,
          autogrow: true,
          value: impact.value || '',
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnFocus: initialBulletPoint,
          onOnBlur: removeBulletPoint,
          onPaste: handlePaste,
          onKeydown: _withKeys(injectBulletPointOnEnter, ["enter"]),
          onOnChange: _cache[6] || (_cache[6] = 
            (event) => {
              setValue('impact', event.detail.value);
              validateImpact(event);
            }
          )
        }, null, 8, ["labelText", "errorText", "placeholderText", "value", "isLoading"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(AppButton, {
            size: "default",
            type: "primary",
            label: _unref(t)('resume.workExperienceForm.saveBtnLabel'),
            ariaLabel: _unref(t)('resume.workExperienceForm.saveBtnLabel'),
            onClick: saveForm,
            "data-test": "save-form-btn",
            class: "save-form-btn",
            width: "100%",
            isLoading: isFormSaving.value,
            disabled: !isFormSavingEnabled.value,
            ariaRoleDescription: saveButtonDescription.value
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled", "ariaRoleDescription"]),
          _createVNode(AppButton, {
            size: "default",
            type: "secondary",
            label: _unref(t)('resume.workExperienceForm.deleteBtnLabel'),
            ariaLabel: _unref(t)('resume.workExperienceForm.deleteBtnLabel'),
            "data-test": "delete-form-btn",
            class: "delete-form-btn",
            onClick: triggerDelete,
            isLoading: isWorkExperienceDeleting.value,
            disabled: !workExperienceId.value,
            width: "100%"
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled"]),
          _createVNode(AppButton, {
            size: "default",
            type: "textOnly",
            label: _unref(t)('resume.workExperienceForm.closeBtnLabel'),
            ariaLabel: _unref(t)('resume.workExperienceForm.closeBtnLabel'),
            "data-test": "close-form-btn",
            class: "close-form-btn",
            id: "closeBtn",
            onClick: returnToPage,
            width: "100%"
          }, null, 8, ["label", "ariaLabel"])
        ])
      ], 8, _hoisted_1)
    ]),
    "resume-helper": _withCtx(() => [...(_cache[7] || (_cache[7] = []))]),
    _: 1
  }, 8, ["breadcrumbs", "skipOnRouteBeforeLeave", "triggerDeleteModal"]))
}
}

})